import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Wallet,
  Bitcoin,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  XCircle,
  History,
  ArrowLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WithdrawalsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wallet/withdrawals`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load withdrawal history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const statusPill = (status) => {
    const config = {
      pending: {
        label: 'Pending',
        bg: 'bg-amber-500/10',
        text: 'text-amber-400',
        border: 'border-amber-500/30',
        icon: Clock,
      },
      paid: {
        label: 'Paid',
        bg: 'bg-emerald-500/10',
        text: 'text-emerald-400',
        border: 'border-emerald-500/30',
        icon: CheckCircle2,
      },
      rejected: {
        label: 'Rejected',
        bg: 'bg-red-500/10',
        text: 'text-red-400',
        border: 'border-red-500/30',
        icon: XCircle,
      },
    }[status] || {
      label: status,
      bg: 'bg-slate-500/10',
      text: 'text-slate-400',
      border: 'border-slate-500/30',
      icon: Clock,
    };
    const Icon = config.icon;
    return (
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${config.bg} ${config.text} ${config.border}`}
        data-testid={`withdrawal-status-${status}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4 sm:px-6 lg:px-8" data-testid="withdrawals-page">
      <div className="max-w-5xl mx-auto">
        {/* Back link */}
        <Link
          to="/wallet"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Wallet
        </Link>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#F7931A]/10 border border-[#F7931A]/30 flex items-center justify-center">
            <History className="w-7 h-7 text-[#F7931A]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">
              Withdrawal <span className="text-gradient-gold">History</span>
            </h1>
            <p className="text-slate-400 mt-1">
              Every payout request you've made and its current status
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="card-dark p-6" data-testid="withdrawal-total-requests">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
              <History className="w-4 h-4" />
              Total Requests
            </div>
            <div className="font-mono text-3xl font-bold">{data?.total_requests || 0}</div>
          </div>
          <div className="card-dark p-6" data-testid="withdrawal-total-pending">
            <div className="flex items-center gap-2 text-sm text-amber-400 mb-2">
              <Clock className="w-4 h-4" />
              Pending
            </div>
            <div className="font-mono text-3xl font-bold text-amber-400">
              {(data?.total_pending_satoshis || 0).toLocaleString()}
              <span className="text-base text-slate-500 ml-2">sats</span>
            </div>
          </div>
          <div className="card-dark p-6" data-testid="withdrawal-total-paid">
            <div className="flex items-center gap-2 text-sm text-emerald-400 mb-2">
              <CheckCircle2 className="w-4 h-4" />
              Paid Out
            </div>
            <div className="font-mono text-3xl font-bold text-emerald-400">
              {(data?.total_paid_satoshis || 0).toLocaleString()}
              <span className="text-base text-slate-500 ml-2">sats</span>
            </div>
          </div>
        </div>

        {/* History Table */}
        {data?.withdrawals?.length > 0 ? (
          <div className="card-dark overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-[#0D0F17] border-b border-white/10 text-xs font-mono uppercase tracking-widest text-slate-500">
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Amount</div>
              <div className="col-span-5">Wallet Address</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">Tx</div>
            </div>

            <div className="divide-y divide-white/5">
              {data.withdrawals.map((w) => (
                <div
                  key={w.id}
                  className="grid md:grid-cols-12 gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors items-center"
                  data-testid={`withdrawal-row-${w.id}`}
                >
                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-500 md:hidden mb-1">Date</div>
                    <div className="text-sm text-slate-300">
                      {new Date(w.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(w.created_at).toLocaleTimeString()}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-500 md:hidden mb-1">Amount</div>
                    <div className="flex items-center gap-1 font-mono font-bold text-[#F7931A]">
                      <Bitcoin className="w-4 h-4" />
                      {w.amount_satoshis.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500">
                      ≈ ${(w.amount_satoshis * 0.0005).toFixed(4)}
                    </div>
                  </div>

                  <div className="md:col-span-5 min-w-0">
                    <div className="text-xs text-slate-500 md:hidden mb-1">Wallet</div>
                    <div className="font-mono text-xs text-slate-400 truncate">
                      {w.btc_wallet_address}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-500 md:hidden mb-1">Status</div>
                    {statusPill(w.status)}
                  </div>

                  <div className="md:col-span-1 md:text-right">
                    {w.tx_hash ? (
                      <a
                        href={`https://mempool.space/tx/${w.tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                      >
                        View
                        <ArrowUpRight className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="card-dark p-12 text-center" data-testid="withdrawals-empty">
            <Wallet className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No Withdrawals Yet</h3>
            <p className="text-slate-400 mb-6">
              Once you request a payout, it will appear here with real-time status.
            </p>
            <Link to="/wallet">
              <Button className="btn-gold">
                Go to Wallet
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {/* Info */}
        <div className="mt-8 card-dark p-6">
          <div className="text-sm text-slate-400 space-y-1">
            <p>
              <span className="text-slate-500">•</span> Withdrawal requests are reviewed manually within 24-48 hours.
            </p>
            <p>
              <span className="text-slate-500">•</span> Once processed, a Bitcoin transaction hash will appear next to the request.
            </p>
            <p>
              <span className="text-slate-500">•</span> Network fees are deducted from the withdrawal amount.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalsPage;
